import { useState, useEffect } from 'react'
import { PlusIcon, TrashIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline'

const API_BASE_URL = '/api/v1/dev'

interface Unit { id: number; name: string; type?: string; is_base?: boolean }
interface UnitWeight {
  id: number
  unit_id: number
  weight_g_per_unit: number | string
  unit?: { id: number; name: string }
}

interface Props {
  ingredientId: number
  units: Unit[]
  defaultUnitId?: number
  onError?: (msg: string) => void
}

/**
 * Gestiona las equivalencias en gramos de otras unidades de trabajo para un
 * ingrediente (p. ej. TBSP/TSP/ml de "Cremor tártaro"). La unidad de compra
 * (por defecto) no se toca; estas equivalencias permiten usar esas unidades
 * en recetas sin errores de conversión.
 */
export default function WorkUnitsManager({ ingredientId, units, defaultUnitId, onError }: Props) {
  const [items, setItems] = useState<UnitWeight[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<UnitWeight | null>(null)
  const [unitId, setUnitId] = useState('')
  const [grams, setGrams] = useState('')
  const [saving, setSaving] = useState(false)

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/unit-weights?ingredient_id=${ingredientId}`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok) setItems(data.data || [])
    } catch {
      onError?.('No se pudieron cargar las equivalencias')
    }
  }

  useEffect(() => { fetchItems() }, [ingredientId])

  // Unidades elegibles según la familia de la unidad por defecto:
  //  - masa por defecto    -> se pueden añadir discretas y de volumen
  //  - volumen por defecto -> solo discretas (volumen↔masa ya lo cubre la densidad)
  //  - discreta por defecto-> solo de volumen
  // Nunca la base (gramo) ni la propia unidad por defecto.
  const defaultType = units.find(u => u.id === defaultUnitId)?.type
  const ALLOWED: Record<string, string[]> = {
    mass: ['count', 'volume'],
    volume: ['count'],
    count: ['volume'],
  }
  const allowedTypes = defaultType ? (ALLOWED[defaultType] || ['count', 'volume']) : ['count', 'volume']
  const selectableUnits = units.filter(u =>
    !u.is_base && u.id !== defaultUnitId && (u.type ? allowedTypes.includes(u.type) : true)
  )
  const unitName = (id: number) => units.find(u => u.id === id)?.name || `#${id}`

  const reset = () => { setEditing(null); setUnitId(''); setGrams(''); setShowForm(false) }
  const openNew = () => { reset(); setShowForm(true) }
  const openEdit = (it: UnitWeight) => {
    setEditing(it); setUnitId(String(it.unit_id)); setGrams(String(it.weight_g_per_unit)); setShowForm(true)
  }

  const save = async () => {
    if (!unitId) { onError?.('Selecciona una unidad'); return }
    const g = parseFloat(grams)
    if (!(g > 0)) { onError?.('Indica los gramos por unidad (mayor que 0)'); return }
    setSaving(true)
    try {
      const url = editing ? `${API_BASE_URL}/unit-weights/${editing.id}` : `${API_BASE_URL}/unit-weights`
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ingredient_id: ingredientId, unit_id: Number(unitId), weight_g_per_unit: g }),
      })
      const data = await res.json()
      if (res.ok && (data.data || data.success !== false)) { reset(); fetchItems() }
      else onError?.(data.message || 'No se pudo guardar la equivalencia')
    } catch {
      onError?.('Error de conexión')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (it: UnitWeight) => {
    if (!confirm(`¿Eliminar la equivalencia de ${it.unit?.name || unitName(it.unit_id)}?`)) return
    try {
      const res = await fetch(`${API_BASE_URL}/unit-weights/${it.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } })
      if (res.ok) fetchItems()
      else onError?.('No se pudo eliminar')
    } catch {
      onError?.('Error de conexión')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Unidades de trabajo (equivalencia a gramos)</label>
        {!showForm && (
          <button type="button" onClick={openNew} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
            <PlusIcon className="h-4 w-4 mr-1" /> Añadir equivalencia
          </button>
        )}
      </div>

      {items.length > 0 ? (
        <div className="space-y-2 mb-3">
          {items.map(it => (
            <div key={it.id} className="border border-gray-200 rounded p-2 flex items-center justify-between text-sm">
              <span className="text-gray-900">
                1 {it.unit?.name || unitName(it.unit_id)} = <span className="font-medium">{parseFloat(String(it.weight_g_per_unit))} g</span>
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => openEdit(it)} className="text-yellow-600 hover:text-yellow-800" title="Editar"><PencilIcon className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(it)} className="text-red-600 hover:text-red-800" title="Eliminar"><TrashIcon className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !showForm && <p className="text-sm text-gray-400 mb-3">Sin equivalencias. Añádelas para poder usar esas unidades (p. ej. cucharadas) en recetas.</p>
      )}

      {showForm && (
        <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">{editing ? 'Editar equivalencia' : 'Nueva equivalencia'}</span>
            <button type="button" onClick={reset} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">1</span>
            <select
              value={unitId}
              onChange={e => setUnitId(e.target.value)}
              disabled={!!editing}
              className="flex-1 border-gray-300 rounded-md shadow-sm text-sm disabled:bg-gray-100"
            >
              <option value="">Unidad…</option>
              {selectableUnits.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">=</span>
            <input
              type="number" step="0.001" min="0" value={grams}
              onChange={e => setGrams(e.target.value)}
              placeholder="gramos"
              className="w-28 border-gray-300 rounded-md shadow-sm text-sm"
            />
            <span className="text-sm text-gray-600">g</span>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={reset} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancelar</button>
            <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Añadir')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
